import { Component, EventEmitter, Input, OnDestroy, Output, forwardRef } from '@angular/core';
import { AddMultipleComponent, IAddedItem } from 'components/input/add-multiple/add-multiple.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { InputType } from 'enums/InputType';
import { LogEntryType } from 'enums/LogEntryType';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { NotificationModel } from 'models/generated/Communication.Api.Models.Notification';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CommunicationService } from 'services/communication.service';
import { FormValidationService } from 'services/form-validation.service';
import { InputContainerService } from 'services/input-container.service';
import { PushService } from 'services/push.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';
import { CommunicationLogComponent } from './communication-log.component';
import { TaskActionMailComponent } from './task-action-mail.component';

@Component({
	selector: 'task-action-log',
	templateUrl: './task-action-log.component.html',
	imports: [Layout, forwardRef(() => TaskActionCommentComponent), TaskActionMailComponent, CommunicationLogComponent, TranslatePipe]
})
export class TaskActionLogComponent implements OnDestroy {

	@Input() showFilters = true;
	@Input() showAllTaskActions = false;
	@Input() allowNewComment = true;
	@Input() allowNewMail = true;

	public logEntryType: typeof LogEntryType = LogEntryType;
	public logFilter: LogEntryType[] = [LogEntryType.Internal, LogEntryType.Mail, LogEntryType.Event];
	public filteredLogEntries: CommunicationLogEntryModel[] = [];
	public showAddComment = false;
	public showSendMail = false;
	public contacts: ContactModel[] = [];
	public loading = true;

	private newLogEntrySubscription: Subscription;
	private _taskAction?: TaskActionModel;

	private _company = new CompanyModel();
	@Input()
	set company(value: CompanyModel) {
		if (!value) return;
		this._company = value;
		this.contacts = this._company.companyContacts.map(cc => cc.contact!);
	}
	get company() { return this._company; }

	@Input()
	set taskAction(action: TaskActionModel | undefined) {
		if (!action || action.id == this.taskAction?.id) return;

		this._taskAction = action;
		this.showSendMail = false;
		this.showAddComment = false;
		this.communicationService.getLogEntriesForTaskflow(action.taskflowId!).then(logEntries => {
			this._communicationLog = logEntries;
			this.applyFilter();
			this.loading = false;
		});
	}
	get taskAction() { return this._taskAction; }

	@Input()
	set taskflowId(id: string | undefined) {
		if (!id) return;
		this.taskService.getTaskflow(id).then(taskflow => {
			if (this.taskAction && taskflow.taskActions.find(ta => ta.id === this.taskAction?.id)) return;
			this.taskAction = taskflow.taskActions[0];
		});
	}

	private _communicationLog?: CommunicationLogEntryModel[];
	@Input()
	set communicationLog(value: CommunicationLogEntryModel[]) {
		this._communicationLog = value;
		this.applyFilter();
	}
	@Output() logUpdated = new EventEmitter();

	constructor(
		pushService: PushService,
		private readonly taskService: TaskService,
		private readonly communicationService: CommunicationService) {

		this.newLogEntrySubscription = pushService.onCommunicationLogEntryChanged.subscribe(async event => {
			if (!this.taskAction || !this._communicationLog) return;

			const data = event.data;
			if (data.taskflowId && data.taskflowId === this.taskAction.taskflowId && !this._communicationLog.find(entry => entry.id == data.logEntryId)) {
				const newLogEntry = await this.communicationService.getLogEntry(data.logEntryId);
				this._communicationLog.unshift(newLogEntry);
				this.logUpdated.emit();
				if (!newLogEntry.taskActionId || newLogEntry.taskActionId == this.taskAction.id) this.applyFilter();
			}
		});
	}

	toggleFilter(logType: LogEntryType) {
		if (this.logFilter.indexOf(logType) > -1)
			this.logFilter.splice(this.logFilter.indexOf(logType), 1);
		else
			this.logFilter.push(logType);

		this.applyFilter();
	}
	private applyFilter() {
		const filteredEntries = (this._communicationLog ?? [])
			.filter(entry => this.showAllTaskActions || !entry.taskActionId || entry.taskActionId === this.taskAction?.id)
			.sort((a, b) => {
				return (b.timestamp ? new Date(b.timestamp).getTime() : new Date(0).getTime()) - (a.timestamp ? new Date(a.timestamp).getTime() : new Date(0).getTime());
			});
		this.filteredLogEntries = this.taskService.filterLogEntries(filteredEntries ?? [], this.logFilter);
	}

	toggleAddMail() {
		this.showSendMail = !this.showSendMail;
		this.showAddComment = false;
		if (this.showSendMail && !this.logFilter.some(lf => lf === LogEntryType.Mail)) {
			this.toggleFilter(LogEntryType.Mail);
		}
	}

	toggleAddComment() {
		this.showAddComment = !this.showAddComment;
		this.showSendMail = false;
		if (this.showAddComment && !this.logFilter.some(lf => lf === LogEntryType.Internal)) {
			this.toggleFilter(LogEntryType.Internal);
		}
	}

	async saveComment(communicationLog: CommunicationLogEntryModel) {
		if (!this.taskAction) return;
		const notifyUsers = communicationLog.notifications.map(notification => { return notification.assignedToUserId!; });
		const companyId = this.taskAction.taskflow?.companyId;
		if (!companyId) throw new Error('Task action does not have a company associated.');

		const newEntry = await this.communicationService.addCommunicationLogEntry(communicationLog.comment, companyId, this.taskAction.taskflowId, this.taskAction.id, notifyUsers);
		if (!this._communicationLog?.find(e => e.id == newEntry.id)) this._communicationLog?.unshift(newEntry); // Check if already inserted due to potential race condition with pushmessages
		this.showAddComment = false;
		this.applyFilter();
		if (!this.logFilter.some(lf => lf === LogEntryType.Internal)) this.toggleFilter(LogEntryType.Internal);
	}

	sendReplyMail = (mail: { comment: string, subject: string, recipients: string[]; }) => this.sendMail(mail);
	private async sendMail(mail: { comment: string, subject: string, recipients: string[]; }) {
		if (!this.taskAction) return;

		const companyId = this.taskAction.taskflow?.companyId;
		if (!companyId) throw new Error('Task action does not have a company associated.');

		const newEntry = await this.communicationService.sendMail(mail.comment, mail.subject, mail.recipients, companyId, this.taskAction.taskflowId, this.taskAction.id, this.taskAction.trackingId);
		if (!this._communicationLog?.find(e => e.id == newEntry.id)) this._communicationLog?.unshift(newEntry); // Check if already inserted due to potential race condition with pushmessages
		this.showSendMail = false;
		this.applyFilter();
		if (!this.logFilter.some(lf => lf === LogEntryType.Mail)) this.toggleFilter(LogEntryType.Mail);
	}
	async sendNewMail(mail: { comment: string, subject: string, recipients: string[]; }) {
		await this.sendMail(mail);
		this.showSendMail = false;
	}

	allowNewCommunication(): boolean {
		return (this.allowNewComment || this.allowNewMail) && !!this.taskAction && !this.taskAction.completed;
	}

	ngOnDestroy() {
		this.newLogEntrySubscription.unsubscribe();
	}
}

@Component({
	selector: 'task-action-comment',
	providers: [InputContainerService, FormValidationService],
	templateUrl: './task-action-comment.component.html',
	imports: [Layout, CustomInputComponent, AddMultipleComponent, TranslatePipe]
})
export class TaskActionCommentComponent {
	inputType = InputType;

	newComment = '';
	notifyUsers: string[] = [];
	userItems: IAddedItem[];

	@Output() onCancel = new EventEmitter<void>();
	@Output() onSave = new EventEmitter<CommunicationLogEntryModel>();

	constructor(userService: UserService, private inputContainer: InputContainerService) {
		this.userItems = userService.activeUsers.map((user) => {
			return { value: user.id!, label: user.name };
		});
		setTimeout(() => {
			this.inputContainer.focus();
		});
	}

	saveComment() {
		let communicationLogEntry: CommunicationLogEntryModel = new CommunicationLogEntryModel();
		communicationLogEntry.comment = this.newComment;

		this.notifyUsers.forEach(userId => {
			let notification = new NotificationModel;
			notification.assignedToUserId = userId;
			communicationLogEntry.notifications.push(notification);
		});
		this.onSave.emit(communicationLogEntry);
	}

	selectedItemsChange(addedItems: IAddedItem[]) {
		this.notifyUsers = addedItems.map((item) => { return item.value });
	}
}
